import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc, arrayUnion, addDoc, getDoc, orderBy, limit, arrayRemove } from 'firebase/firestore';
import { useNotifications } from '../contexts/NotificationsContext';
import { useDrag } from '@use-gesture/react';
import { animated, useSpring } from 'react-spring';

const EMOTIONS = {
  "🤗": "hug",
  "👋": "wave",
  "💪": "strength",
  "🎉": "celebration",
  "❤️": "love",
  "✨": "sparkles",
  "🌟": "star",
  "🙌": "praise"
};

export default function Friends() {
  const { currentUser } = useAuth();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [activeEmojiPicker, setActiveEmojiPicker] = useState(null);
  const emojiPickerRef = useRef(null);
  const { showToast } = useNotifications();

  useEffect(() => {
    function handleClickOutside(event) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setActiveEmojiPicker(null);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setActiveEmojiPicker(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Fetch friends and their goals
  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const friendIds = userDoc.data()?.friends || [];

        const friendsData = await Promise.all(
          friendIds.map(async (friendId) => {
            const friendRef = doc(db, 'users', friendId);
            const friendDoc = await getDoc(friendRef);
            const friendData = friendDoc.data();

            // Fetch friend's goals summary - modified to get only last 2 updated goals
            const goalsRef = collection(db, 'goals');
            const goalsQuery = query(
              goalsRef,
              where('userId', '==', friendId),
              where('status', '==', 'active'),
              where('isPrivate', '==', false),
              orderBy('lastModified', 'desc'),
              limit(2)
            );
            const goalsSnap = await getDocs(goalsQuery);
            const goals = goalsSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));

            // Fetch friend's latest mood
            const reflectionsRef = collection(db, 'reflections');
            const moodQuery = query(
              reflectionsRef,
              where('userId', '==', friendId),
              orderBy('timestamp', 'desc'),
              limit(1)
            );
            const moodSnap = await getDocs(moodQuery);
            const latestMood = !moodSnap.empty ? {
              mood: moodSnap.docs[0].data().mood?.name || moodSnap.docs[0].data().mood,
              emoji: moodSnap.docs[0].data().mood?.emoji
            } : null;

            return {
              id: friendId,
              ...friendData,
              goals,
              latestMood
            };
          })
        );

        setFriends(friendsData);
      } catch (error) {
        console.error('Error fetching friends:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchFriends();
    }
  }, [currentUser]);

  const handleSearch = async () => {
    if (!searchEmail.trim()) return;

    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef, 
        where('email', '==', searchEmail.toLowerCase()),
        where('uid', '!=', currentUser.uid) // Don't show current user
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userData = {
          id: querySnapshot.docs[0].id,
          ...querySnapshot.docs[0].data()
        };

        // Check if already friends
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const currentFriends = userDoc.data()?.friends || [];

        if (currentFriends.includes(userData.id)) {
          setSearchResult('already_friends');
        } else {
          setSearchResult(userData);
        }
      } else {
        setSearchResult('not_found');
      }
    } catch (error) {
      console.error('Error searching user:', error);
      setSearchResult('error');
    } finally {
      setLoading(false);
    }
  };

  const sendEmotion = async (friendId, emoji, emotion) => {
    try {
      // Find the friend in our friends array
      const friend = friends.find(f => f.id === friendId);
      if (!friend) return;

      // Send the notification to the database
      const notificationRef = collection(db, 'notifications');
      await addDoc(notificationRef, {
        type: 'emotion',
        from: currentUser.uid,
        fromName: currentUser.displayName,
        to: friendId,
        emoji,
        emotion,
        timestamp: new Date(),
        read: false
      });

      // Close emoji picker
      setActiveEmojiPicker(null);

      // Show toast notification
      showToast(`Sent ${emoji} to ${friend.displayName}`, 'emotion');
    } catch (error) {
      console.error('Error sending emotion:', error);
    }
  };

  const addFriend = async (friendId) => {
    if (!currentUser || !friendId) return;
    
    try {
      // Add to current user's friends
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        friends: arrayUnion(friendId)
      });

      // Add current user to friend's friends list
      const friendRef = doc(db, 'users', friendId);
      await updateDoc(friendRef, {
        friends: arrayUnion(currentUser.uid)
      });

      // Fetch the friend's data and add to state
      const friendDoc = await getDoc(friendRef);
      const friendData = {
        id: friendId,
        ...friendDoc.data(),
        goals: [] // Initially empty, will be updated in useEffect
      };

      setFriends(prev => [...prev, friendData]);
      setSearchResult(null);
      setSearchEmail('');
    } catch (error) {
      console.error('Error adding friend:', error);
    }
  };

  const getMoodEmoji = (mood) => {
    const moodEmojis = {
      'VERY_HAPPY': '😊',
      'HAPPY': '🙂',
      'NEUTRAL': '😐',
      'SAD': '😔',
      'VERY_SAD': '😢',
      'EXCITED': '🤩',
      'ANXIOUS': '😰',
      'PEACEFUL': '😌',
      'STRESSED': '😫'
    };
    return moodEmojis[mood] || '😶';
  };

  const removeFriend = async (friendId) => {
    try {
      // Remove from current user's friends list
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        friends: arrayRemove(friendId)
      });

      // Remove current user from friend's friends list
      const friendRef = doc(db, 'users', friendId);
      await updateDoc(friendRef, {
        friends: arrayRemove(currentUser.uid)
      });

      // Update local state
      setFriends(prev => prev.filter(f => f.id !== friendId));
    } catch (error) {
      console.error('Error removing friend:', error);
    }
  };

  const FriendCard = ({ friend }) => {
    const [{ x }, api] = useSpring(() => ({ x: 0 }));

    const bind = useDrag(({ down, movement: [mx], velocity: [vx], cancel }) => {
      // Only allow left swipe (negative movement)
      const swipeX = Math.min(0, mx);
      const trigger = vx < -0.2 || swipeX < -100;
      
      if (trigger && !down) {
        // Confirm removal
        if (window.confirm(`Remove ${friend.displayName} from friends?`)) {
          removeFriend(friend.id);
        } else {
          // Reset position if user cancels
          api.start({ x: 0 });
        }
      } else {
        // Update position based on drag
        api.start({ 
          x: down ? swipeX : 0,
          immediate: down
        });
      }
    }, { 
      axis: 'x',
      rubberband: true,
      bounds: { right: 0 }
    });

    return (
      <div className="relative">
        {/* Delete Background */}
        <div className="absolute inset-0 bg-red-500 rounded-2xl flex items-center justify-end pr-8">
          <svg 
            className="w-8 h-8 text-white" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
            />
          </svg>
        </div>

        {/* Card Content */}
        <animated.div
          {...bind()}
          style={{
            x,
            touchAction: 'pan-y',
            position: 'relative',
            background: 'white',
            borderRadius: '1rem',
            zIndex: 1,
            backgroundColor: x.to(
              [0, -100], 
              ['rgb(255, 255, 255)', 'rgb(254, 242, 242)']
            ),
          }}
          className="bg-white p-6 rounded-2xl shadow-sm"
        >
          <div className="flex items-start justify-between mb-6">
            <div className="flex gap-4">
              <img
                src={`https://api.dicebear.com/7.x/adventurer-neutral/svg?seed=${encodeURIComponent(friend.email)}`}
                alt="Avatar"
                className="w-12 h-12 rounded-full"
              />
              <div className="space-y-1">
                <h3 className="font-medium text-gray-900">{friend.displayName}</h3>
                <p className="text-sm text-gray-500">{friend.email}</p>
                {friend.latestMood && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                    {friend.latestMood.emoji || getMoodEmoji(friend.latestMood.mood)} 
                    <span className="ml-1 capitalize">
                      {friend.latestMood.mood.toLowerCase().replace(/_/g, ' ')}
                    </span>
                  </span>
                )}
              </div>
            </div>
            <div className="relative">
              <button
                onClick={() => setActiveEmojiPicker(activeEmojiPicker === friend.id ? null : friend.id)}
                className="p-2.5 text-primary-600 hover:bg-primary-50 rounded-full transition-colors"
                title="Send emotion"
              >
                🤗
              </button>
              
              {activeEmojiPicker === friend.id && (
                <div 
                  ref={emojiPickerRef}
                  className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg p-3 z-50 border border-gray-200 w-[200px]"
                  style={{ transform: 'translateY(0)' }}
                >
                  <div className="relative">
                    <h4 className="text-xs font-medium text-gray-500 mb-2 text-center">Send emotion</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {Object.entries(EMOTIONS).map(([emoji, emotion]) => (
                        <button
                          key={emotion}
                          onClick={() => sendEmotion(friend.id, emoji, emotion)}
                          className="p-2 hover:bg-gray-50 rounded-lg transition-colors text-xl flex items-center justify-center"
                          title={emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Friend's Goals */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">Current Goals</h4>
              <span className="text-xs text-gray-500">
                {friend.goals.length > 0 && 
                  `Showing ${Math.min(2, friend.goals.length)} public goals`
                }
              </span>
            </div>
            {friend.goals.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No active goals</p>
            ) : (
              <>
                {friend.goals.map((goal) => (
                  <div key={goal.id} className="bg-gray-50 p-3 rounded-xl">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h5 className="font-medium text-gray-800">{goal.title}</h5>
                        <span className="text-xs text-gray-500 capitalize">{goal.category}</span>
                      </div>
                      <span className="text-sm font-medium text-primary-600">
                        {goal.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-primary-600 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${goal.progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </animated.div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-1 pb-20 no-select">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Friends</h1>
          <p className="mt-2 text-gray-600">Support and inspire each other</p>
        </div>

        {/* Add Friend */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Add Friend</h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 min-w-0">
              <input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="Enter friend's email"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              className="shrink-0 p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 justify-center"
            >
              {/* <svg 
                className="w-6 h-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                />
              </svg> */}
              Search
            </button>
          </div>

          {/* Search Results */}
          {searchResult && (
            <div className="mt-4">
              {searchResult === 'not_found' ? (
                <p className="text-center text-gray-500">No user found with this email</p>
              ) : searchResult === 'already_friends' ? (
                <p className="text-center text-gray-500">You are already friends with this user</p>
              ) : searchResult === 'error' ? (
                <p className="text-center text-red-500">An error occurred while searching</p>
              ) : (
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img
                        src={`https://api.dicebear.com/7.x/adventurer-neutral/svg?seed=${encodeURIComponent(searchResult.email)}`}
                        alt="Avatar"
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <h3 className="font-medium text-gray-900">{searchResult.displayName}</h3>
                        <p className="text-sm text-gray-500">{searchResult.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => addFriend(searchResult.id)}
                      className="px-3 py-1 bg-primary-100 text-primary-600 rounded-lg hover:bg-primary-200 transition-colors"
                    >
                      Add Friend
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Friends List */}
        <div className="space-y-4">
          {loading ? (
            // Loading skeleton
            [...Array(3)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl shadow-sm animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-4 w-32 bg-gray-200 rounded"></div>
                    <div className="h-3 w-24 bg-gray-200 rounded"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 w-full bg-gray-200 rounded"></div>
                  <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))
          ) : friends.length === 0 ? (
            <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="text-4xl mb-2">👋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No friends yet</h3>
              <p className="text-gray-500">Start by adding your first friend!</p>
            </div>
          ) : (
            friends.map((friend) => (
              <FriendCard key={friend.id} friend={friend} />
            ))
          )}
        </div>
      </div>
    </div>
  );
} 